import React from "react"

import { useRecoilState, useRecoilValue } from "recoil"

import {
  type NewEquipment as EquipmentTypeInferredFromZod,
  newEquipmentSchema,
} from "~/containers/Proposal/state/designs/design-model"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { useGetEquipmentForProposalQuery } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"

import {
  allEquipmentForSiteState,
  availableNonPrimaryEquipmentTypeIndex,
  batteriesArePresentState,
  batteriesForSiteState,
  inverterBrandOptionsState,
  invertersArePresentState,
  invertersForSiteState,
  solarModulesArePresentState,
  solarModulesForSiteState,
} from "./equipment-state"
import { proposalLocalState } from "../proposal/proposal-state"
import { useSiteContext } from "../site/site-context"

const FetchEquipmentForSiteContext = React.createContext<ReturnType<
  typeof useFetchEquipmentForSite
> | null>(null)

export const FetchEquipmentForSiteContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const site = useSiteContext()
  const equipment = useFetchEquipmentForSite(site.local.id)

  return (
    <FetchEquipmentForSiteContext.Provider value={equipment}>
      {children}
    </FetchEquipmentForSiteContext.Provider>
  )
}

export const useFetchEquipmentForSiteContext = (): ReturnType<
  typeof useFetchEquipmentForSite
> => {
  const equipment = React.useContext(FetchEquipmentForSiteContext)

  if (!equipment) {
    throw new Error(`FetchEquipmentForSiteContext not found`)
  }

  return equipment
}

const useFetchEquipmentForSite = (
  siteId: string,
): {
  solarModules: EquipmentTypeInferredFromZod[]
  solarModulesHaveLoaded: boolean
  inverters: EquipmentTypeInferredFromZod[]
  inverterBrandOptions: string[]
  invertersHaveLoaded: boolean
  batteries: EquipmentTypeInferredFromZod[]
  batteriesHaveLoaded: boolean
  availableNonPrimaryEquipmentTypeIds: Set<number>
  refetch: ReturnType<typeof useGetEquipmentForProposalQuery>[`refetch`]
  isLoading: boolean
} => {
  const proposalId = useRecoilValue(proposalLocalState).id
  const solarModules = useRecoilValue(solarModulesForSiteState)
  const inverters = useRecoilValue(invertersForSiteState)
  const batteries = useRecoilValue(batteriesForSiteState)
  const availableNonPrimaryEquipmentTypeIds = useRecoilValue(
    availableNonPrimaryEquipmentTypeIndex,
  )

  const solarModulesHaveLoaded = useRecoilValue(solarModulesArePresentState)
  const invertersHaveLoaded = useRecoilValue(invertersArePresentState)
  const batteriesHaveLoaded = useRecoilValue(batteriesArePresentState)

  const inverterBrandOptions = useRecoilValue(inverterBrandOptionsState)
  const [, setAllEquipmentForSite] = useRecoilState(allEquipmentForSiteState)

  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const { refetch, isLoading, isRefetching } = useGetEquipmentForProposalQuery(
    client,
    {
      proposalId: proposalId,
      equipmentType: null,
      inverterManufacturerId: null,
    },
    {
      enabled: !!proposalId,
      staleTime: Infinity,
      onError,
      onSuccess: (data) => {
        if (!data.equipmentForProposal) {
          throw new Error(`No equipment found for site ${siteId}`)
        }

        // map through zod schema to make sure it is the type we want
        const equipmentForSite: EquipmentTypeInferredFromZod[] =
          data.equipmentForProposal.map((equipment) =>
            newEquipmentSchema.parse(equipment),
          )

        setAllEquipmentForSite(equipmentForSite)
      },
    },
  )

  return {
    isLoading: isLoading || isRefetching,
    solarModules,
    solarModulesHaveLoaded,
    inverters,
    inverterBrandOptions,
    invertersHaveLoaded,
    batteries,
    batteriesHaveLoaded,
    availableNonPrimaryEquipmentTypeIds,
    refetch,
  }
}
